/**
 * Profilseite — Konto-Übersicht + Self-Service-Einstellungen.
 *
 * Aufbau: Profil-Header (Initialen + Rollen-Badge) und danach thematisch
 * gruppierte Sektionen (Lohn & Abrechnung, Benachrichtigungen, Verwaltung).
 */

import { Link } from 'react-router-dom';
import {
  useAuth,
  useCanManage,
  useCanManageAnyLocation,
  useActiveMembership,
  useDisplayRole,
  useIsAdminChefView,
  DISPLAY_ROLE_META,
} from '../store/auth';
import { formatMoney } from '../lib/format';
import { SalarySettingsCard } from '../components/SalarySettingsCard';
import { NotificationsCard } from '../components/NotificationsCard';
import { DevicesCard } from '../components/DevicesCard';
import { AccountDeletionCard } from '../components/AccountDeletionCard';
import { ChangeEmailCard, ChangePasswordCard } from '../components/ChangePasswordCard';
import { RecoveryCodeCard } from '../components/WageKey';
import { SwapOffersCard } from '../components/SwapOffersCard';
import { OnboardingTutorial } from '../components/OnboardingTutorial';
import { useState } from 'react';

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export default function HomePage() {
  const user = useAuth((s) => s.user);
  const [showTutorial, setShowTutorial] = useState(false);
  const isOwner = useCanManage();
  const canManageAnyLocation = useCanManageAnyLocation();
  const activeMembership = useActiveMembership();
  const displayRole = useDisplayRole();
  const isAdminChefView = useIsAdminChefView();
  if (!user) return null;

  const role = DISPLAY_ROLE_META[displayRole];
  // v1.3.0: Admin mit gewählter Firma sieht die Chef-Ansicht.
  const roleLabel = displayRole === 'admin' && isAdminChefView ? 'Admin · Chef-Ansicht' : role.label;

  return (
    <div className="max-w-md lg:max-w-2xl mx-auto mt-4 sm:mt-6 space-y-6 pb-12">
      {/* Profil-Header */}
      <section className="card p-4">
        <div className="flex items-center gap-4">
          <div
            className="w-14 h-14 rounded-full bg-brand/20 text-brand-light flex items-center justify-center text-xl font-bold shrink-0"
            aria-hidden
          >
            {initialsOf(user.name)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-lg font-semibold truncate">{user.name}</p>
            <p className="text-sm text-slate-400 truncate">{user.email}</p>
            <span className={`badge mt-1 inline-block ${role.cls}`}>{roleLabel}</span>
          </div>
        </div>

        {(user.salaryMode === 'salary' || user.billingCycleStartDay !== 1) && (
          <div className="mt-3 pt-3 border-t border-border/60 space-y-1">
            {user.salaryMode === 'salary' && (
              <p className="text-xs text-slate-400">
                Festgehalt{' '}
                <span className="text-emerald-400 font-medium">
                  {formatMoney(user.monthlySalary)}
                </span>{' '}
                für {user.monthlyTargetHours.toFixed(2).replace('.', ',')} Std.
              </p>
            )}
            {user.billingCycleStartDay !== 1 && (
              <p className="text-xs text-slate-400">
                Abrechnungszeitraum ab Tag {user.billingCycleStartDay} des Monats.
              </p>
            )}
          </div>
        )}
      </section>

      {/* Einladungs-Code (nur wenn man Leitung einer Firma ist) */}
      {activeMembership?.joinCode && (
        <section className="card p-4">
          <p className="text-sm font-medium">Einladungs-Code · {activeMembership.companyName}</p>
          <div className="flex items-center gap-2 mt-1">
            <code className="text-base font-mono bg-slate-900 rounded px-2 py-0.5 tracking-widest">
              {activeMembership.joinCode}
            </code>
            <button
              onClick={() => void navigator.clipboard?.writeText(activeMembership.joinCode ?? '')}
              className="text-xs text-brand-light hover:text-brand !min-h-0"
            >
              kopieren
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Neue Mitarbeiter registrieren sich mit diesem Code auf der Anmeldeseite.
          </p>
        </section>
      )}

      {/* Lohn & Abrechnung — ein Eintrag, Bearbeiten öffnet ein Menü */}
      <SalarySettingsCard />

      {/* Benachrichtigungen — ein Eintrag, Bearbeiten öffnet ein Menü */}
      <NotificationsCard />

      {/* Geräte (Phase W, Kapitel 3.5) — zwischen Benachrichtigungen und
          Hilfe platziert, direkt nach der Notifications-Karte. */}
      <DevicesCard />

      {/* Passwort ändern (Kapitel 3.4, Entscheidung #31) */}
      <ChangePasswordCard />
      <ChangeEmailCard />
      {/* Phase L (Kapitel 7.1): Wiederherstellungs-Code der Lohn-Verschlüsselung (nur Chef) */}
      <RecoveryCodeCard isChef={isOwner} />

      {/* Phase HH (Kapitel 20.6, Entscheidung #43) — sichtbar für Mitarbeiter
          UND Filialleitung, bewusst NICHT für den Chef (alleiniger
          Wiederherstellungs-Anker der Firmen-Lohndaten). */}
      {!isOwner && <AccountDeletionCard />}

      {/* Tausch-Angebote (Kapitel 6, Phase E) — nur sichtbar, wenn es welche gibt */}
      <SwapOffersCard />

      {/* Verwaltung — sichtbar für Chef (Owner) UND Filialleitung/Bereichsleiter (Kapitel 1.3).
          Team-Übersicht bleibt bewusst Chef-exklusiv. "Standorte & Schicht-Vorlagen" ist seit
          dem LocationsAdminPage-Umbau auch für Filialleitung sichtbar — dort sehen sie nur ihre
          eigenen Standorte (rein lesend) und pflegen ausschließlich deren Vorlagen. */}
      {canManageAnyLocation && (
        <Section title="Verwaltung">
          {(user.role === 'admin' || user.role === 'superadmin') && (
            <NavCard
              to="/admin/companies"
              title="Firmen verwalten"
              desc="Firmen anlegen, Mitarbeiter & Standorte zuordnen, Einladungs-Codes."
            />
          )}
          <NavCard to="/wochenplan" title="Wochenplan erstellen" desc="Schichten fürs Team planen und veröffentlichen." />
          {isOwner && (
            <NavCard to="/team" title="Team-Übersicht" desc="Stunden, Lohn und Auszahlung je Mitarbeiter, Anträge, Export." />
          )}
          <NavCard
            to="/admin/users"
            title="Mitarbeiter verwalten"
            desc={
              isOwner
                ? 'Konten, Rollen, Stundenlöhne und Standort-Zuordnungen.'
                : 'Konten und Standort-Zuordnungen an deinen eigenen Standorten.'
            }
          />
          <NavCard
            to="/admin/locations"
            title="Standorte & Schicht-Vorlagen"
            desc={
              isOwner
                ? 'Standorte anlegen und Vorlagen pflegen.'
                : 'Schicht-Vorlagen an deinen eigenen Standorten pflegen.'
            }
          />
          {isOwner && (
            <NavCard
              to="/admin/einstellungen"
              title="Firmen-Einstellungen"
              desc="Minijob-Grenze deiner Firma und weitere Einstellungen."
            />
          )}
        </Section>
      )}

      {/* App */}
      <Section title="App">
        <NavCard to="/download" title="App-Download & Version" desc="ShiftSync als PWA installieren." />
      </Section>

      {/* Hilfe (Phase R+AA, Kapitel 21.5) — bewusst AUSSERHALB der Chef/Filialleiter-
          exklusiven "Verwaltung"-Sektion, weil das Tutorial für ALLE Rollen gilt. Ein
          Tap öffnet den vollstaendigen Karten-Stapel fuer die aktuelle Rolle manuell —
          setzt keinen der drei Zeitstempel zurueck, rein manueller Aufruf. */}
      <Section title="Hilfe">
        <button
          type="button"
          onClick={() => setShowTutorial(true)}
          className="card p-3.5 flex items-center gap-3 hover:bg-surface-2 transition !min-h-0 w-full text-left"
        >
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">Tutorial erneut anzeigen</p>
            <p className="text-xs text-slate-400 truncate">Einfuehrung fuer deine aktuelle Rolle noch einmal ansehen.</p>
          </div>
          <span className="text-slate-500 text-lg shrink-0" aria-hidden>
            ›
          </span>
        </button>
      </Section>

      {showTutorial && <OnboardingTutorial manual onClose={() => setShowTutorial(false)} />}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <h3 className="text-xs uppercase tracking-wider text-slate-400 px-1">{title}</h3>
      {children}
    </section>
  );
}

function NavCard({ to, title, desc }: { to: string; title: string; desc: string }) {
  return (
    <Link
      to={to}
      className="card p-3.5 flex items-center gap-3 hover:bg-surface-2 transition !min-h-0"
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-slate-400 truncate">{desc}</p>
      </div>
      <span className="text-slate-500 text-lg shrink-0" aria-hidden>
        ›
      </span>
    </Link>
  );
}
